import React, { useState } from 'react';
import SEO from '../../components/SEO';
import PageBanner from '../../components/PageBanner';

import { motion, AnimatePresence } from 'framer-motion';

import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';

const Overview = () => {
    const { language } = useLanguage();
    const t = translations[language].overview;

    const [activeBase, setActiveBase] = useState(0);

    const nextBase = () => {
        setActiveBase((prev) => (prev + 1) % t.productionBases.length);
    };

    const prevBase = () => {
        setActiveBase((prev) => (prev - 1 + t.productionBases.length) % t.productionBases.length);
    };

    return (
        <div className="pt-20 lg:pt-[120px] min-h-screen bg-white">
            <SEO
                title="BAIC International Overview | Global Automotive Leader"
                description="Explore BAIC's global presence, production bases, and commitment to innovation. Learn who we are and our achievements."
                canonicalUrl="https://baicoman.com/about/overview"
            />
            <h1 className="sr-only">BAIC International Overview</h1>
            <PageBanner
                image="/assets/images/about/overview/new/bg.webp"
                imageAlt="BAIC Building"
            >
                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="text-2xl md:text-7xl font-bold text-white mb-4 tracking-tight"
                    style={{
                        textShadow: "0 4px 12px rgba(0,0,0,0.9)"
                    }}
                >
                    BAIC
                </motion.h2>
            </PageBanner>

            {/* 1. Who We Are */}
            <section className="relative w-full pt-20 pb-0 bg-white overflow-hidden">
                <div className="container mx-auto px-6 mb-0">
                    <motion.h2
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-4xl md:text-5xl font-bold mb-20 uppercase text-left text-slate-900"
                    >
                        {t.whoWeAreTitle}
                    </motion.h2>

                    {/* Timeline */}
                    <div className="relative w-full max-w-6xl mx-auto h-64 hidden md:block">
                        {/* Line */}
                        <div className="absolute top-1/2 left-0 w-full h-px bg-gray-200 -translate-y-1/2"></div>

                        <div className="grid grid-cols-7 gap-4 h-full">
                            {t.statsGrid.map((item, idx) => (
                                <div key={idx} className="relative h-full flex justify-center items-center group">
                                    {/* Dot */}
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        whileInView={{ scale: 1 }}
                                        transition={{ delay: idx * 0.1, duration: 0.3 }}
                                        viewport={{ once: true }}
                                        className="absolute w-3 h-3 bg-slate-500 rounded-full z-10"
                                    ></motion.div>

                                    {/* Content */}
                                    <motion.div
                                        initial={{ opacity: 0, y: item.pos === 'top' ? 20 : -20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.1 + 0.2, duration: 0.5 }}
                                        viewport={{ once: true }}
                                        className={`absolute flex flex-col items-center text-center w-40 ${item.pos === 'top' ? 'bottom-1/2 pb-8' : 'top-1/2 pt-8'
                                            }`}
                                    >
                                        {item.pos === 'top' ? (
                                            <>
                                                <span className="text-xs text-gray-500 uppercase tracking-tight mb-2 font-medium">{item.label}</span>
                                                <span className="text-3xl font-bold text-slate-700">{item.val}</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-3xl font-bold text-slate-700 mb-2">{item.val}</span>
                                                <span className="text-xs text-gray-500 uppercase tracking-tight font-medium">{item.label}</span>
                                            </>
                                        )}
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mobile View (Vertical List) */}
                    <div className="md:hidden space-y-8">
                        {t.statsGrid.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                viewport={{ once: true }}
                                className="flex justify-between items-center border-b border-gray-100 pb-4"
                            >
                                <span className="text-gray-500 text-sm uppercase">{item.label}</span>
                                <span className="text-2xl font-bold text-slate-800">{item.val}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Background Image placed at bottom */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="w-full relative "
                >
                    <img
                        src="/assets/images/about/overview/new/who_we_are-bg3.webp"
                        alt="Who We Are Background"
                        className="w-full h-auto object-cover max-h-[500px]"
                    />
                    <div className="absolute bottom-0 left-0 right-0 px-8 pb-8 flex justify-center">
                        <img
                            src="/assets/images/about/overview/new/who_we_are-icon.webp"
                            alt="Brand Logos"
                            className="max-w-4xl w-full h-auto object-contain "
                        />
                    </div>
                </motion.div>
            </section>

            {/* 2. Awards */}
            <section className="py-20 bg-white">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="container mx-auto px-6 text-center max-w-4xl"
                >
                    <h2 className="text-4xl font-bold mb-8 uppercase">{t.awardsTitle}</h2>
                    <p className="text-gray-600 leading-relaxed text-lg">
                        {t.awardsText}
                    </p>
                </motion.div>
            </section>

            {/* 3. Top Gear */}
            <section className="py-20 bg-slate-50">
                <div className="container mx-auto px-6">
                    <div className="flex flex-col md:flex-row items-center gap-12">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="w-full md:w-1/3"
                        >
                            <h2 className="text-4xl font-bold mb-6 uppercase">{t.topGearTitle}</h2>
                            <p className="text-gray-600 leading-relaxed text-lg text-justify">
                                {t.topGearText}
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="w-full  flex justify-center md:justify-end gap-6 flex-wrap"
                        >
                            <img src="/assets/images/about/overview/new/TGSA.webp" alt="Top Gear Award" className="h-32 md:h-40 object-contain" />
                            <img src="/assets/images/about/overview/new/JDPower.webp" alt="JD Power" className="h-32 md:h-40 object-contain" />
                            <img src="/assets/images/about/overview/new/IDA.webp" alt="IDA" className="h-32 md:h-40 object-contain" />
                            <img src="/assets/images/about/overview/new/BDC.webp" alt="BDC" className="h-32 md:h-40 object-contain" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 4. Production Bases Carousel */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeBase}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.5 }}
                            className="flex flex-col md:flex-row gap-12 items-center"
                        >
                            <div className="w-full md:w-1/2 rounded-2xl overflow-hidden shadow-xl aspect-video relative">
                                <img
                                    src={t.productionBases[activeBase].image}
                                    alt={t.productionBases[activeBase].title}
                                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="w-full md:w-1/2 space-y-8">
                                <h2 className="text-4xl font-bold mb-8 uppercase">{t.productionBases[activeBase].title}</h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {t.productionBases[activeBase].stats.map((stat, idx) => (
                                        <div key={idx}>
                                            <h4 className="text-sm text-gray-500 uppercase tracking-widest mb-1">{stat.label}</h4>
                                            <p className="text-xl md:text-2xl font-bold text-slate-800">{stat.value}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-4 mt-8">
                                    <button onClick={prevBase} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                                        <span className="text-red-600 text-4xl font-bold leading-none select-none">&lt;</span>
                                    </button>
                                    <button onClick={nextBase} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                                        <span className="text-red-600 text-4xl font-bold leading-none select-none">&gt;</span>
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>


        </div>
    );
};

export default Overview;
